import {
  diffOrderItemChanges,
  hasKitchenPrinterConfigured,
  isAutoPrintModificationEnabled,
  isLikelySnapshotMismatch,
  printKitchenReceiptForOrder,
} from './printService';

export const STORAGE_PRINT_STATION_ENABLED = 'pos_print_station_enabled';

/** Counter roles that may print when no remote_print_user_id is saved on the branch. */
const REMOTE_PRINT_COUNTER_ROLES = new Set([
  'branch_manager',
  'manager',
  'restaurant_manager',
]);

const LOCAL_PRINT_SUPPRESS_MS = 15000;
/** Block duplicate remote create prints (double order_created / race). */
const REMOTE_CREATE_PRINT_TTL_MS = 15 * 60 * 1000;
/** Skip false modification tickets right after create (snapshot key mismatch). */
const REMOTE_CREATE_GRACE_MS = 45000;
const STORAGE_REMOTE_CREATE_PRINTED = 'pos_remote_create_printed_';

/** @type {Map<string, { create?: number, modify?: number }>} */
const localPrintSuppress = new Map();
/** @type {Map<string, number>} orderId -> remote create printed at */
const remoteCreatePrintedAt = new Map();
/** @type {Set<string>} orderIds currently printing (blocks duplicate socket events) */
const remoteCreateInFlight = new Set();

const isElectronRuntime = () =>
  typeof window !== 'undefined' && !!window.electronAPI?.isElectron;

export const isPrintStationEnabled = () => {
  if (!isElectronRuntime()) return false;
  try {
    const value = localStorage.getItem(STORAGE_PRINT_STATION_ENABLED);
    if (value === null) return true;
    return value !== 'false';
  } catch (_) {
    return true;
  }
};

export const setPrintStationEnabled = (enabled) => {
  try {
    localStorage.setItem(STORAGE_PRINT_STATION_ENABLED, enabled ? 'true' : 'false');
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
    import('./socketService').then(({ default: socketService }) => {
      socketService.registerPrintStation();
    }).catch(() => {});
  }
};

const normalizeOrderId = (orderOrId) => {
  if (orderOrId == null) return '';
  if (typeof orderOrId === 'object') {
    return String(orderOrId._id || orderOrId.id || '');
  }
  return String(orderOrId);
};

export const markOrderPrintedLocally = (orderOrId, kind = 'both') => {
  const id = normalizeOrderId(orderOrId);
  if (!id) return;
  const now = Date.now();
  const prev = localPrintSuppress.get(id) || {};
  if (kind === 'create' || kind === 'both') prev.create = now;
  if (kind === 'modify' || kind === 'both') prev.modify = now;
  localPrintSuppress.set(id, prev);
};

export const shouldSuppressRemotePrint = (orderOrId, kind = 'create') => {
  const id = normalizeOrderId(orderOrId);
  if (!id) return false;
  const entry = localPrintSuppress.get(id);
  if (!entry) return false;
  const ts = kind === 'modify' ? entry.modify : entry.create;
  if (!ts) return false;
  if (Date.now() - ts > LOCAL_PRINT_SUPPRESS_MS) {
    if (kind === 'modify') delete entry.modify;
    else delete entry.create;
    if (!entry.create && !entry.modify) localPrintSuppress.delete(id);
    return false;
  }
  return true;
};

/** True when this counter session placed the order (skip duplicate socket print). */
export const isOrderPlacedByCurrentUser = (order, currentUser) => {
  const uid = getCurrentUserId(currentUser);
  if (!uid || !order) return false;
  const serverId = order.serverId ?? order.server_id;
  if (serverId == null) return false;
  const sid =
    typeof serverId === 'object'
      ? serverId._id || serverId.id
      : serverId;
  return sid != null && String(sid) === String(uid);
};

export const shouldSkipRemoteSocketPrint = (order, currentUser, eventKind = 'create') => {
  if (isOrderPlacedByCurrentUser(order, currentUser)) return true;
  if (shouldSuppressRemotePrint(order, eventKind)) return true;
  return false;
};

export const hasRemoteCreateAlreadyPrinted = (orderOrId) => {
  const id = normalizeOrderId(orderOrId);
  if (!id) return false;
  const mem = remoteCreatePrintedAt.get(id);
  if (mem && Date.now() - mem < REMOTE_CREATE_PRINT_TTL_MS) return true;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_REMOTE_CREATE_PRINTED}${id}`);
    if (raw) {
      const ts = parseInt(raw, 10);
      if (ts && Date.now() - ts < REMOTE_CREATE_PRINT_TTL_MS) return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
};

export const markRemoteCreatePrinted = (orderOrId) => {
  const id = normalizeOrderId(orderOrId);
  if (!id) return;
  const now = Date.now();
  remoteCreatePrintedAt.set(id, now);
  try {
    sessionStorage.setItem(`${STORAGE_REMOTE_CREATE_PRINTED}${id}`, String(now));
  } catch (_) {
    /* ignore */
  }
};

/** Reserve a create-print slot (returns false if duplicate / in flight / already done). */
export const tryAcquireRemoteCreatePrint = (orderOrId) => {
  const id = normalizeOrderId(orderOrId);
  if (!id) return false;
  if (hasRemoteCreateAlreadyPrinted(id) || remoteCreateInFlight.has(id)) return false;
  remoteCreateInFlight.add(id);
  return true;
};

export const releaseRemoteCreatePrint = (orderOrId) => {
  const id = normalizeOrderId(orderOrId);
  if (id) remoteCreateInFlight.delete(id);
};

const isWithinRemoteCreateGrace = (orderOrId) => {
  const id = normalizeOrderId(orderOrId);
  if (!id) return false;
  const mem = remoteCreatePrintedAt.get(id);
  if (mem && Date.now() - mem < REMOTE_CREATE_GRACE_MS) return true;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_REMOTE_CREATE_PRINTED}${id}`);
    if (raw) {
      const ts = parseInt(raw, 10);
      if (ts && Date.now() - ts < REMOTE_CREATE_GRACE_MS) return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
};

export const resolveTableForOrder = (order, tables = []) => {
  const tableId = order?.tableId || order?.table_id;
  if (tableId && Array.isArray(tables)) {
    const found = tables.find((tbl) => String(tbl._id || tbl.id) === String(tableId));
    if (found) return found;
  }
  const tableNum = order?.tableNumber ?? order?.table_number;
  if (tableNum != null && tableNum !== '') {
    if (Array.isArray(tables) && tables.length) {
      const byNum = tables.find(
        (tbl) =>
          String(tbl.number) === String(tableNum) ||
          String(tbl.displayName) === String(tableNum)
      );
      if (byNum) return byNum;
    }
    return { number: tableNum, displayName: String(tableNum) };
  }
  return null;
};

const cloneItemsSnapshot = (items) => {
  if (!Array.isArray(items)) return [];
  try {
    return JSON.parse(JSON.stringify(items));
  } catch {
    return items.map((item) => ({ ...item }));
  }
};

export const snapshotOrderItems = (order) =>
  cloneItemsSnapshot(order?.items || order?.orderItems || []);

export const getCurrentUserId = (currentUser) => {
  if (!currentUser) return '';
  return String(currentUser._id || currentUser.id || currentUser.userId || '').trim();
};

/**
 * Branch setting: designated user prints remote orders.
 * If none is configured, any branch/manager role on the counter may print (single-counter setups).
 */
export const isDesignatedRemotePrintUser = (remotePrintUserId, currentUser) => {
  if (!currentUser) return false;
  const uid = getCurrentUserId(currentUser);
  if (!uid) return false;

  const designated =
    remotePrintUserId == null || remotePrintUserId === ''
      ? null
      : String(remotePrintUserId).trim();

  if (!designated) {
    return REMOTE_PRINT_COUNTER_ROLES.has(String(currentUser.role || ''));
  }
  return designated === uid;
};

export const canRunRemotePrintStation = (remotePrintUserId, currentUser) =>
  isElectronRuntime() &&
  isPrintStationEnabled() &&
  hasKitchenPrinterConfigured() &&
  isDesignatedRemotePrintUser(remotePrintUserId, currentUser);

/** Why remote print did not run (null = would run). For diagnostics. */
export const getRemotePrintSkipReason = (remotePrintUserId, currentUser) => {
  if (!isElectronRuntime()) return 'not_electron';
  if (!isPrintStationEnabled()) return 'print_station_disabled';
  if (!hasKitchenPrinterConfigured()) return 'no_kitchen_printer';
  if (!isDesignatedRemotePrintUser(remotePrintUserId, currentUser)) {
    const designated =
      remotePrintUserId != null && String(remotePrintUserId).trim() !== '';
    return designated ? 'not_designated_user' : 'no_remote_print_user_configured';
  }
  return null;
};

const canRunPrintStation = (remotePrintUserId, currentUser) =>
  !getRemotePrintSkipReason(remotePrintUserId, currentUser);

/** Serialize kitchen prints so concurrent socket events do not break the print service. */
let remotePrintChain = Promise.resolve();

export const enqueueRemoteKitchenPrint = (job) => {
  remotePrintChain = remotePrintChain
    .then(() => job())
    .catch((err) => {
      console.error('[print-station] Print queue error:', err?.message || err);
    });
  return remotePrintChain;
};

async function hydrateOrderForRemotePrint(order, branchId = null, options = {}) {
  const { alwaysFetch = false } = options;
  const items = order?.items || order?.orderItems || [];
  const orderId = normalizeOrderId(order);

  if (!alwaysFetch && items.length > 0) return order;
  if (!orderId) return order;

  let confirmed404 = false;
  try {
    const { default: orderService } = await import('./orderService');
    const full = await orderService.getOrderById(orderId);
    const fullItems = full?.items || full?.orderItems || [];
    if (full && fullItems.length > 0) {
      console.info(
        '[print-station] Loaded order from API for kitchen print:',
        orderId,
        fullItems.length,
        'items'
      );
      return full;
    }
  } catch (err) {
    confirmed404 = err?.response?.status === 404;
    console.warn(
      '[print-station] getOrderById failed, trying active orders:',
      orderId,
      err?.response?.status || err?.message
    );
  }

  if (branchId) {
    try {
      const { default: orderService } = await import('./orderService');
      const active = await orderService.getActiveOrders(branchId);
      const found = (active || []).find(
        (o) => String(o._id || o.id) === String(orderId)
      );
      if (found) {
        const foundItems = found.items || found.orderItems || [];
        console.info(
          '[print-station] Loaded order from active list:',
          orderId,
          foundItems.length,
          'items'
        );
        return found;
      }
    } catch (err) {
      console.warn('[print-station] active orders fallback failed:', orderId, err?.message);
    }
  }

  // The server definitively said this order does not exist and it isn't in
  // the active list either. Printing from the stale event payload here is how
  // ghost orders produced repeated kitchen tickets — signal "do not print".
  if (confirmed404) return null;

  return order;
}

/**
 * @param {object} params
 * @param {object} params.order
 * @param {Array} params.tables
 * @param {Function} params.t
 * @param {string|null} params.language
 * @param {string|null} params.timezone
 * @param {string|null} params.remotePrintUserId
 * @param {object|null} params.currentUser
 */
export async function handleRemoteOrderCreated({
  order,
  tables,
  t,
  language,
  timezone,
  remotePrintUserId,
  currentUser,
  branchId = null,
  enableCounterItems = true,
}) {
  if (!canRunPrintStation(remotePrintUserId, currentUser) || !order) {
    return;
  }

  const orderId = normalizeOrderId(order);
  if (shouldSuppressRemotePrint(orderId, 'create')) {
    console.warn('[print-station] Skipped create (recent local print):', orderId);
    return;
  }

  const status = String(order.status || '').toLowerCase();
  if (status && status !== 'confirmed') {
    console.warn('[print-station] Skipped (status not confirmed):', orderId, status);
    return;
  }

  const hydrated = await hydrateOrderForRemotePrint(order, branchId);
  if (!hydrated) {
    console.warn('[print-station] Order not found on server — skipping kitchen print:', orderId);
    return;
  }
  const resolvedBranchId =
    branchId || hydrated?.branchId || hydrated?.branch_id || order?.branchId || order?.branch_id;
  const itemCount = (hydrated?.items || hydrated?.orderItems || []).length;
  const table = resolveTableForOrder(hydrated, tables);

  try {
    const { persistRemoteOrderSnapshot } = await import('./remotePrintSocketHandler');
    persistRemoteOrderSnapshot(orderId, hydrated);

    // Match counter POS: no branch logo on kitchen tickets (avoids slow/failed logo fetch).
    await printKitchenReceiptForOrder(
      hydrated,
      table,
      t,
      null,
      language,
      null,
      timezone,
      enableCounterItems,
      resolvedBranchId
    );
    markOrderPrintedLocally(orderId, 'create');
    markRemoteCreatePrinted(orderId);
    console.info('[print-station] Kitchen receipt sent:', orderId, `(${itemCount} items)`);
  } catch (err) {
    console.error('[print-station] Remote order_created kitchen print failed:', orderId, err);
    throw err;
  }
}

/**
 * @param {object} params
 * @param {object} params.order
 * @param {object|null} params.previousOrder
 * @param {Array} params.tables
 * @param {Function} params.t
 * @param {string|null} params.language
 * @param {string|null} params.timezone
 * @param {boolean} [params.autoPrintModification]
 * @param {string|null} params.remotePrintUserId
 * @param {object|null} params.currentUser
 */
export async function handleRemoteOrderUpdated({
  order,
  previousOrder,
  tables,
  t,
  language,
  timezone,
  autoPrintModification = isAutoPrintModificationEnabled(),
  remotePrintUserId,
  currentUser,
  branchId = null,
}) {
  if (!canRunPrintStation(remotePrintUserId, currentUser) || !order || !previousOrder) {
    return;
  }
  if (!autoPrintModification) {
    console.warn('[print-station] Modification print disabled in POS settings');
    return;
  }

  const orderId = normalizeOrderId(order);
  if (shouldSuppressRemotePrint(orderId, 'modify')) {
    console.warn('[print-station] Skipped modification (recent local print):', orderId);
    return;
  }

  const previousItems = previousOrder.items || previousOrder.orderItems || [];
  const hydrated = await hydrateOrderForRemotePrint(order, branchId, { alwaysFetch: true });
  if (!hydrated) {
    console.warn('[print-station] Order not found on server — skipping modification print:', orderId);
    return;
  }

  // hydrateOrderForRemotePrint(alwaysFetch: true) always re-fetches the order
  // from the REST API, which never persists the transient noPrint flag (it's
  // only ever attached to the socket payload). Re-apply it here from the
  // original socket `order` so a no-print item marked during a remote
  // modification is still skipped on the printed ticket.
  const noPrintKeys = new Set(
    (order.items || order.orderItems || [])
      .filter((it) => it?.noPrint)
      .map((it) => it.cartItemId || it.menuItemId)
  );
  const rawCurrentItems = hydrated?.items || hydrated?.orderItems || order.items || order.orderItems || [];
  const currentItems = noPrintKeys.size
    ? rawCurrentItems.map((it) =>
        noPrintKeys.has(it.cartItemId || it.menuItemId) ? { ...it, noPrint: true } : it
      )
    : rawCurrentItems;
  const hydratedForPrint = noPrintKeys.size ? { ...hydrated, items: currentItems } : hydrated;
  const changes = diffOrderItemChanges(previousItems, currentItems);
  const { added, removed, quantityChanged } = changes;

  if (
    isWithinRemoteCreateGrace(orderId) &&
    isLikelySnapshotMismatch(previousItems, currentItems, changes)
  ) {
    console.warn(
      '[print-station] Skipped modification (snapshot mismatch right after create):',
      orderId
    );
    const { persistRemoteOrderSnapshot } = await import('./remotePrintSocketHandler');
    persistRemoteOrderSnapshot(orderId, hydrated);
    return;
  }

  if (!added.length && !removed.length && !quantityChanged.length) {
    console.warn('[print-station] Modification skipped (no item changes detected):', orderId, {
      previousCount: previousItems.length,
      currentCount: currentItems.length,
    });
    return;
  }

  const table = resolveTableForOrder(hydrated, tables);
  const resolvedBranchId =
    branchId || hydrated?.branchId || hydrated?.branch_id || order?.branchId || order?.branch_id;

  try {
    const { printModificationKitchenForOrder } = await import('./printService');
    await printModificationKitchenForOrder(
      hydratedForPrint,
      table,
      previousItems,
      t,
      language,
      timezone,
      null,
      true,
      resolvedBranchId
    );
    console.info('[print-station] Modification ticket sent:', orderId, {
      added: added.length,
      removed: removed.length,
      qtyChanged: quantityChanged.length,
    });
  } catch (err) {
    console.error('[print-station] Remote order_updated modification print failed:', orderId, err);
    throw err;
  }
}
